import React, { useState, useEffect } from 'react';
import { Truck, Wrench, Settings, MapPin, Eye, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../App';

const OwnedEquipment = () => {
  const { user } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFuelType, setFilterFuelType] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);

  useEffect(() => {
    fetchOwnedEquipment();
    fetchSyncStatus();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dispatch/equipment-sync-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (r.ok) setSyncInfo(await r.json());
    } catch { /* non-critical */ }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dispatch/equipment-sync-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Synced from dispatch sheet — ${d.added} added, ${d.updated} updated, ${d.removed} removed`);
        fetchOwnedEquipment();
        fetchSyncStatus();
      } else {
        toast.error(d.detail || 'Sync failed');
      }
    } catch {
      toast.error('Sync failed');
    }
    setSyncing(false);
  };

  const fetchOwnedEquipment = async () => {
    setIsLoading(true);
    try {
      // For now, we'll fetch from the API. You might need to create a specific owned equipment endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owned-equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const equipmentData = await response.json();
        setEquipment(equipmentData);
      } else {
        toast.error('Failed to load owned equipment');
      }
    } catch (error) {
      console.error('Error fetching owned equipment:', error);
      toast.error('Failed to load owned equipment');
    }
    setIsLoading(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { color: 'bg-green-100 text-green-800' },
      assigned: { color: 'bg-blue-100 text-blue-800' },
      maintenance: { color: 'bg-red-100 text-red-800' },
      unavailable: { color: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || statusConfig.available;
    return (
      <Badge className={config.color}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Available'}
      </Badge>
    );
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'vehicles':
        return <Truck className="h-6 w-6" />;
      case 'welders':
        return <Wrench className="h-6 w-6" />;
      default:
        return <Settings className="h-6 w-6" />;
    }
  };

  const filteredEquipment = equipment.filter(item => {
    if (filterCategory !== 'all' && item.equipment_category !== filterCategory) return false;
    if (filterFuelType !== 'all' && item.fuel_type !== filterFuelType) return false;
    return true;
  });

  // Get unique categories and fuel types from equipment
  const categories = ['all', ...new Set(equipment.map(item => item.equipment_category).filter(Boolean))];
  const fuelTypes = ['all', ...new Set(equipment.map(item => item.fuel_type).filter(Boolean))];

  const canManage = ['super_admin', 'admin', 'project_manager_estimator', 'dispatch'].includes(user?.role);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Owned Equipment</h1>
          <p className="text-slate-700 mt-1">365ISI company-owned equipment and assets</p>
        </div>
        {['super_admin', 'admin', 'dispatch', 'office_admin'].includes(user?.role) && (
          <div className="text-right">
            <Button onClick={syncNow} disabled={syncing} data-testid="equipment-sync-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync from Dispatch Sheet'}
            </Button>
            <p className="text-xs text-slate-500 mt-1" data-testid="equipment-sync-last">
              Auto-syncs monthly{syncInfo?.last_sync_at ? ` · Last: ${new Date(syncInfo.last_sync_at).toLocaleString()}` : ''}
            </p>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Settings className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Equipment</p>
                <p className="text-2xl font-bold text-gray-900">{equipment.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Truck className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Vehicles</p>
                <p className="text-2xl font-bold text-gray-900">
                  {equipment.filter(item => item.equipment_category === 'vehicles').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Wrench className="h-8 w-8 text-orange-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Welders</p>
                <p className="text-2xl font-bold text-gray-900">
                  {equipment.filter(item => item.equipment_category === 'welders').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Settings className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-gray-900">
                  {equipment.filter(item => !item.status || item.status === 'available').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-col space-y-4">
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Filter by Category</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={filterCategory === category ? 'default' : 'outline'}
                  onClick={() => setFilterCategory(category)}
                  className="capitalize"
                >
                  {category === 'all' ? 'All Categories' : category}
                </Button>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Filter by Fuel Type</h3>
            <div className="flex flex-wrap gap-2">
              {fuelTypes.map(fuelType => (
                <Button
                  key={fuelType}
                  variant={filterFuelType === fuelType ? 'default' : 'outline'}
                  onClick={() => setFilterFuelType(fuelType)}
                  className="capitalize"
                >
                  {fuelType === 'all' ? 'All Fuel Types' : fuelType}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Equipment List */}
      <div className="grid gap-6">
        {filteredEquipment.map(item => (
          <Card key={item.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4">
                    <div className="text-blue-500">
                      {getCategoryIcon(item.equipment_category)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{item.equipment_name}</h3>
                      <p className="text-gray-600">{item.equipment_type}</p>
                      {item.equipment_category && (
                        <p className="text-sm text-blue-600 capitalize">Category: {item.equipment_category}</p>
                      )}
                      {item.fuel_type && (
                        <p className="text-sm text-green-700">Fuel: {item.fuel_type}</p>
                      )}
                      {item.model && <p className="text-sm text-gray-500">Model: {item.model}</p>}
                      {item.serial_number && <p className="text-sm text-gray-500">S/N: {item.serial_number}</p>}
                    </div>
                    <div className="ml-6">
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                  
                  {item.current_project_title && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-800">Currently Assigned</p>
                      <p className="text-sm text-blue-600">Project: {item.current_project_title}</p>
                      {item.assigned_to && <p className="text-sm text-blue-600">Assigned to: {item.assigned_to}</p>}
                    </div>
                  )}

                  {item.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{item.notes}</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col space-y-2 ml-6">
                  {canManage && (
                    <>
                      <Button variant="outline" className="w-full">
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                      
                      <Button variant="outline" className="w-full">
                        <MapPin className="h-4 w-4 mr-2" />
                        Assign to Project
                      </Button>
                      
                      <Button variant="outline" className="w-full">
                        <Settings className="h-4 w-4 mr-2" />
                        Maintenance
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEquipment.length === 0 && (
        <div className="text-center py-12">
          <Settings className="h-12 w-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No equipment found</h3>
          <p className="text-gray-600">No equipment matches your current filters.</p>
        </div>
      )}
    </div>
  );
};

export default OwnedEquipment;